import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LinkAdmin, listLinks } from "../api";

interface Props {
  onCreateNew: () => void;
}

export default function AdminLinks({ onCreateNew }: Props) {
  const nav = useNavigate();
  const [links, setLinks] = useState<LinkAdmin[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    listLinks()
      .then(setLinks)
      .catch((e) => {
        if (String(e.message).includes("401")) nav("/admin/login");
        else setErr(e.message);
      });
  }, [nav]);

  const copy = async (url: string, id: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const waShare = (url: string, title: string, recipientName?: string) => {
    const who = recipientName ? `Hey ${recipientName}, ` : "";
    const msg = `${who}record me a video on "${title}" → ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Shareable links</h2>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-charcoal text-ivory rounded-lg text-sm font-medium hover:bg-black transition"
        >
          + New link
        </button>
      </div>

      {err && <div className="text-red-600">{err}</div>}
      {!links && !err && <div className="text-concrete">Loading…</div>}

      {links && links.length === 0 && (
        <div className="text-concrete py-12 text-center bg-white rounded-xl">
          No links yet.{" "}
          <button
            onClick={onCreateNew}
            className="underline text-charcoal hover:text-black"
          >
            Create your first one
          </button>
          .
        </div>
      )}

      {links && links.length > 0 && (
        <ul className="space-y-3">
          {links.map((l) => (
            <li
              key={l.link_id}
              className="bg-white rounded-xl shadow-sm p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-bold text-lg">{l.title}</span>
                    {l.recipient_name && (
                      <span className="text-sm text-concrete">
                        → {l.recipient_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        l.mode === "nominative"
                          ? "bg-lemonade/30 text-charcoal"
                          : "bg-stone text-concrete"
                      }`}
                    >
                      {l.mode}
                    </span>
                    {l.has_briefing && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-charcoal text-ivory">
                        ▶ briefing
                      </span>
                    )}
                    <span className="text-xs text-concrete">
                      {l.video_count} {l.video_count === 1 ? "reply" : "replies"}
                    </span>
                  </div>
                  <div className="text-sm text-concrete mt-2 line-clamp-2">
                    {l.text}
                  </div>
                  <div className="text-xs text-concrete mt-2">
                    {new Date(l.created_at).toLocaleString()}
                    {l.expires_at &&
                      ` · expires ${new Date(l.expires_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-stone">
                <input
                  readOnly
                  value={l.url}
                  className="flex-1 px-3 py-2 text-xs bg-ivory rounded border border-stone font-mono"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  onClick={() => copy(l.url, l.link_id)}
                  className="px-3 py-2 text-sm bg-stone text-charcoal rounded hover:bg-ash transition"
                >
                  {copiedId === l.link_id ? "✓ Copied" : "Copy"}
                </button>
                <button
                  onClick={() => waShare(l.url, l.title, l.recipient_name)}
                  className="px-3 py-2 text-sm bg-[#25D366] text-white rounded hover:brightness-95 transition"
                >
                  WhatsApp
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
